import { useMemo } from "react";
import {
  Select,
  Button,
  Popover,
  InputNumber,
  Badge,
  Tooltip,
  DatePicker,
} from "antd";
import dayjs from "dayjs";
import { InfoCircleOutlined, FilterOutlined } from "@ant-design/icons";
import { useVictimsAndKillers } from "../../../hooks/useVictimsAndKillers";
import HighlightPanel from "../HighlightPanel/HighlightPanel";
import NotesPanel from "../NotesPanel/NotesPanel";
import styled from "styled-components";
import {
  DEFAULT_VICTIM_MAP_FILTER,
  countActiveVictimMapFilters,
  type VictimMapFilter,
} from "../../../utils/map/victimMapFilter";

export interface MapControlsProps {
  onSelectTarget: (target: { lat: number; lng: number } | null) => void;
  onOpenInfo: () => void;
  victimFilter: VictimMapFilter;
  onVictimFilterChange: (next: VictimMapFilter) => void;
}

// Styled components
const ControlsWrapper = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 500;
  pointer-events: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  max-width: calc(100% - 32px);
`;

const TopRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
  }
`;

const SearchGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    order: 1;
  }
`;

const FilterContent = styled.div`
  width: 300px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  background: #2a2f38;
  border: 1px solid #222831;
  border-radius: 12px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.35);
  color: #eeeeee;
`;

const FilterHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding-bottom: 6px;
  border-bottom: 2px solid rgba(142, 22, 22, 0.7);
`;

const FilterTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #eeeeee;
`;

const FilterBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: #8e1616;
  color: #ffffff;
  font-size: 11px;
  font-weight: 700;
`;

const FilterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FilterSectionTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(238, 238, 238, 0.72);
`;

const FilterRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;

const FilterActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 2px;
`;

const HighlightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    order: 2;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;

  @media (max-width: 768px) {
    order: 2;
  }
`;

const SearchVictimsSelect = styled(Select)`
  min-width: 200px;
  flex: 1;
  width: auto;

  .ant-select-selector {
    height: 34px !important;
    display: flex;
    align-items: center;
    padding: 0 12px !important;

    background: #393e46 !important;
    border-color: #222831 !important;
    color: #eeeeee !important;
    border-radius: 8px;
  }

  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    color: #eeeeee !important;
    line-height: 34px !important;
    opacity: 0.9;
  }

  .ant-select-selection-search-input {
    height: 34px !important;
  }

  .ant-select-arrow svg,
  .ant-select-clear .anticon svg {
    fill: #eeeeee;
    color: #eeeeee;
  }

  &:hover .ant-select-selector {
    border-color: #8e1616 !important;
  }
  &.ant-select-focused .ant-select-selector {
    border-color: #8e1616 !important;
    box-shadow: 0 0 0 2px rgba(142, 22, 22, 0.2);
  }

  /* Dropdown */
  .ant-select-dropdown {
    background: #393e46;
    color: #eeeeee;
    border: 1px solid #222831;
  }
  .ant-select-item {
    color: #eeeeee;
  }
  .ant-select-item-option-active,
  .ant-select-item-option-selected {
    background: #222831;
    color: #eeeeee;
  }
`;

const InfoButton = styled(Button)`
  height: 34px;
  min-width: 34px;
  padding: 0;
  border-radius: 8px;

  background: #393e46 !important;
  color: #eeeeee !important;
  border: 1px solid #8e1616 !important;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  & > span {
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
  }

  &:hover {
    background: #222831 !important;
    border-color: #8e1616 !important;
    box-shadow: 0 0 0 2px rgba(142, 22, 22, 0.2);
  }
  svg {
    height: 16px;
    width: 16px;
  }
`;

const FilterButton = styled(Button)<{ $active: boolean }>`
  height: 34px;
  min-width: 34px;
  width: 34px;
  border-radius: 8px;
  padding: 0;

  background: #393e46 !important;
  color: #eeeeee !important;
  border: 1px solid #222831 !important;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  & > span {
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
  }

  &:hover {
    background: #222831 !important;
    border-color: #8e1616 !important;
    box-shadow: 0 0 0 2px rgba(142, 22, 22, 0.2);
  }
`;

const FilterSelect = styled(Select)`
  width: 100%;

  .ant-select-selector {
    background: #393e46 !important;
    border-color: #222831 !important;
    color: #eeeeee !important;
    border-radius: 8px !important;
    min-height: 32px !important;
    box-shadow: none !important;
  }

  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    color: #eeeeee !important;
  }

  .ant-select-arrow,
  .ant-select-clear {
    color: #eeeeee !important;
  }

  &:hover .ant-select-selector,
  &.ant-select-focused .ant-select-selector {
    border-color: #8e1616 !important;
    box-shadow: 0 0 0 2px rgba(142, 22, 22, 0.2);
  }

  .ant-select-dropdown {
    background: #393e46;
    border: 1px solid #222831;
    border-radius: 8px;
  }

  .ant-select-item {
    color: #eeeeee;
  }

  .ant-select-item-option-active,
  .ant-select-item-option-selected {
    background: #222831 !important;
    color: #ffffff;
  }
`;

const FilterDatePickerWrap = styled.div`
  width: 100%;

  .ant-picker {
    width: 100%;
    background: #393e46;
    border-color: #222831;
    color: #eeeeee;
  }

  .ant-picker-input > input {
    color: #eeeeee;
    caret-color: #eeeeee;
  }

  .ant-picker-input > input::placeholder {
    color: #eeeeee;
    opacity: 0.7;
  }

  .ant-picker-suffix .anticon svg {
    fill: #eeeeee;
    color: #eeeeee;
  }

  .ant-picker-clear .anticon svg {
    fill: #eeeeee;
    color: #eeeeee;
  }

  .ant-picker:hover {
    border-color: #8e1616;
  }
  .ant-picker-focused {
    border-color: #8e1616;
    box-shadow: 0 0 0 2px rgba(142, 22, 22, 0.2);
  }

  .ant-picker-panel {
    background: #393e46;
    color: #eeeeee;
  }
  .ant-picker-cell .ant-picker-cell-inner {
    color: #eeeeee;
  }
  .ant-picker-header,
  .ant-picker-footer {
    background: #393e46;
    color: #eeeeee;
    border-color: #222831;
  }
`;

const FilterNumber = styled(InputNumber)`
  width: 100%;

  &.ant-input-number,
  .ant-input-number {
    width: 100% !important;
    height: 32px !important;
    background: #393e46 !important;
    border: 1px solid #222831 !important;
    border-radius: 8px !important;
    color: #eeeeee !important;
    box-shadow: none !important;
  }

  .ant-input-number-input {
    height: 30px !important;
    background: #393e46 !important;
    color: #eeeeee !important;
    padding: 0 11px !important;
    font-size: 14px !important;
    -webkit-text-fill-color: #eeeeee !important;
  }

  .ant-input-number-input::placeholder {
    color: #eeeeee !important;
    opacity: 0.7;
  }

  .ant-input-number-handler-wrap {
    display: none !important;
  }

  .ant-input-number-handler {
    display: none !important;
  }

  &:hover .ant-input-number,
  &.ant-input-number-focused .ant-input-number {
    border-color: #8e1616 !important;
    box-shadow: 0 0 0 2px rgba(142, 22, 22, 0.2) !important;
  }
`;

const ResetButton = styled(Button)`
  background: #393e46 !important;
  color: #eeeeee !important;
  border: none !important;
  border-radius: 8px;

  &:hover {
    color: #8e1616 !important;
    background: #222831 !important;
  }
`;

export default function MapControls({
  onSelectTarget,
  onOpenInfo,
  victimFilter,
  onVictimFilterChange,
}: MapControlsProps) {
  const { filters: rows, victimsByFilter: victimsByRow } =
    useVictimsAndKillers();

  const updateVictimFilter = (patch: Partial<VictimMapFilter>) => {
    onVictimFilterChange({ ...victimFilter, ...patch });
  };

  const victimIndex = useMemo(() => {
    const idx = new Map<
      string | number,
      { lat: number; lng: number; label: string }
    >();
    for (const row of rows) {
      const victims = victimsByRow[row.id] ?? [];
      for (const v of victims) {
        if (typeof v.lat !== "number" || typeof v.lng !== "number") continue;
        const id = v.id as string | number | undefined;
        const label = v.name ?? String(id ?? "");
        if (id != null) idx.set(id, { lat: v.lat, lng: v.lng, label });
      }
    }
    return idx;
  }, [rows, victimsByRow]);

  const victimOptions = useMemo(() => {
    const opts = Array.from(victimIndex.entries()).map(([value, v]) => ({
      value,
      label: v.label,
    }));
    opts.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
    );
    return opts;
  }, [victimIndex]);

  const availableStatuses = useMemo(() => {
    const set = new Set<string>();
    for (const row of rows) {
      const victims = victimsByRow[row.id] ?? [];
      for (const v of victims) {
        if (v.status) set.add(String(v.status));
      }
    }
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
      .map((value) => ({ value, label: value }));
  }, [rows, victimsByRow]);

  const availableGenders = useMemo(() => {
    const set = new Set<string>();
    for (const row of rows) {
      const victims = victimsByRow[row.id] ?? [];
      for (const v of victims) {
        const gender = v.gender?.trim();
        if (gender) set.add(gender);
      }
    }
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
      .map((value) => ({ value, label: value }));
  }, [rows, victimsByRow]);

  const activeFilterCount = countActiveVictimMapFilters(victimFilter);

  const filterPanel = (
    <FilterContent>
      <FilterHeader>
        <FilterTitle>Filter victims</FilterTitle>
        <FilterBadge>{activeFilterCount}</FilterBadge>
      </FilterHeader>

      <FilterSection>
        <FilterSectionTitle>Status</FilterSectionTitle>
        <FilterSelect
          mode="multiple"
          allowClear
          placeholder="Any status"
          options={availableStatuses}
          value={victimFilter.statuses}
          onChange={(values) =>
            updateVictimFilter({
              statuses: Array.isArray(values) ? (values as string[]) : [],
            })
          }
        />
      </FilterSection>

      <FilterSection>
        <FilterSectionTitle>Gender</FilterSectionTitle>
        <FilterSelect
          mode="multiple"
          allowClear
          placeholder="Any gender"
          options={availableGenders}
          value={victimFilter.genders}
          onChange={(values) =>
            updateVictimFilter({
              genders: Array.isArray(values) ? (values as string[]) : [],
            })
          }
        />
      </FilterSection>

      <FilterSection>
        <FilterSectionTitle>Age Range</FilterSectionTitle>
        <FilterRow>
          <FilterNumber
            min={0}
            placeholder="Min"
            value={victimFilter.ageMin}
            onChange={(value) =>
              updateVictimFilter({
                ageMin: typeof value === "number" ? value : null,
              })
            }
          />
          <FilterNumber
            min={0}
            placeholder="Max"
            value={victimFilter.ageMax}
            onChange={(value) =>
              updateVictimFilter({
                ageMax: typeof value === "number" ? value : null,
              })
            }
          />
        </FilterRow>
      </FilterSection>

      <FilterSection>
        <FilterSectionTitle>Date Of Crime</FilterSectionTitle>
        <FilterRow>
          <FilterDatePickerWrap>
            <DatePicker
              placeholder="From date"
              value={
                victimFilter.dateFrom ? dayjs(victimFilter.dateFrom) : null
              }
              onChange={(_value, dateString) => {
                const next = Array.isArray(dateString)
                  ? (dateString[0] ?? null)
                  : dateString || null;
                updateVictimFilter({ dateFrom: next });
              }}
            />
          </FilterDatePickerWrap>
          <FilterDatePickerWrap>
            <DatePicker
              placeholder="To date"
              value={victimFilter.dateTo ? dayjs(victimFilter.dateTo) : null}
              onChange={(_value, dateString) => {
                const next = Array.isArray(dateString)
                  ? (dateString[0] ?? null)
                  : dateString || null;
                updateVictimFilter({ dateTo: next });
              }}
            />
          </FilterDatePickerWrap>
        </FilterRow>
      </FilterSection>

      <FilterActionRow>
        <ResetButton
          onClick={() => onVictimFilterChange(DEFAULT_VICTIM_MAP_FILTER)}
        >
          Reset Filters
        </ResetButton>
      </FilterActionRow>
    </FilterContent>
  );

  return (
    <ControlsWrapper>
      <TopRow>
        <LeftColumn>
          <HighlightGroup>
            <HighlightPanel />
          </HighlightGroup>
          <HighlightGroup>
            <NotesPanel />
          </HighlightGroup>
        </LeftColumn>

        <SearchGroup>
          <SearchVictimsSelect
            showSearch
            allowClear
            placeholder="Search victim..."
            options={victimOptions}
            filterOption={(input, option) =>
              (option?.label as string)
                ?.toLowerCase()
                .includes(input.toLowerCase())
            }
            onSelect={(value) => {
              const v = victimIndex.get(value as string | number);
              if (v) onSelectTarget({ lat: v.lat, lng: v.lng });
            }}
            onClear={() => onSelectTarget(null)}
          />
          <Tooltip
            title={
              activeFilterCount > 0
                ? `Victim filters (${activeFilterCount} active)`
                : "Victim filters"
            }
          >
            <Popover
              content={filterPanel}
              trigger="click"
              placement="bottomRight"
              overlayStyle={{
                ["--antd-arrow-background-color" as any]: "#2a2f38",
              }}
              overlayInnerStyle={{
                padding: 0,
                background: "transparent",
                borderRadius: 12,
              }}
            >
              <Badge
                count={activeFilterCount > 0 ? activeFilterCount : 0}
                color="#8e1616"
                size="small"
                showZero={false}
                overflowCount={99}
              >
                <FilterButton
                  aria-label="Victim filters"
                  $active={activeFilterCount > 0}
                  icon={<FilterOutlined />}
                />
              </Badge>
            </Popover>
          </Tooltip>
          <InfoButton
            aria-label="Info"
            icon={<InfoCircleOutlined />}
            onClick={onOpenInfo}
          />
        </SearchGroup>
      </TopRow>
    </ControlsWrapper>
  );
}
